import React, { useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { AlertCircle, CheckCircle2, Trash2, UploadCloud } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { Dropzone, type PickedFile } from '@components/Dropzone';
import { ProgressBar } from '@components/ProgressBar';
import { FileTypeIcon } from '@components/FileTypeIcon';
import documentService, { flattenFolders } from '@services/documentService';
import { DOCUMENT_ACCEPT, isUploadCancelled, titleFromFileName, uploadDocumentFile, validateDocumentFile } from '@services/documentUpload';
import type { Document, DocumentFolder, DocumentRefType } from '@/types/document';
import { formatBytes } from '@/utils/formatters';

export interface UploadPrefill {
  folder_id?: number;
  ref_type?: DocumentRefType;
  ref_id?: number;
  /** Trava a pasta (ficha da arma, documento CAC). */
  lockFolder?: boolean;
}

interface Props {
  /** Árvore de pastas (se omitida, o modal carrega). */
  folders?: DocumentFolder[];
  prefill?: UploadPrefill;
  /** Um arquivo só (ex.: arquivo do documento CAC). */
  single?: boolean;
  onClose: () => void;
  onUploaded: (docs: Document[]) => void;
  onError: (err: unknown) => void;
}

interface Item {
  key: string;
  file: File;
  title: string;
  problem: string | null;
  status: 'pending' | 'uploading' | 'done' | 'error';
  progress: number;
  message?: string;
  doc?: Document;
}

const labelCls = 'text-label block mb-1';

/**
 * Envio de documentos: escolhe um ou vários arquivos (validados antes: tipo e 50 MB), metadados
 * comuns (pasta, datas, aviso, tags, descrição) e título por arquivo. Fluxo por arquivo:
 * POST /documents/uploads → PUT no Blob com progresso → SHA-256 → complete.
 */
export const DocumentUploadModal: React.FC<Props> = ({ folders: foldersProp, prefill, single = false, onClose, onUploaded, onError }) => {
  const [folders, setFolders] = useState<DocumentFolder[]>(foldersProp ?? []);
  const [items, setItems] = useState<Item[]>([]);
  const [folderId, setFolderId] = useState<number | ''>(prefill?.folder_id ?? '');
  const [docDate, setDocDate] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [remindDays, setRemindDays] = useState(30);
  const [tags, setTags] = useState('');
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (foldersProp) {
      setFolders(foldersProp);
      return;
    }
    documentService.getFolders().then(setFolders).catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foldersProp]);

  useEffect(() => {
    if (folderId === '' && folders.length > 0 && !prefill?.folder_id) setFolderId(folders[0].id);
  }, [folders, folderId, prefill?.folder_id]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const flat = useMemo(() => flattenFolders(folders), [folders]);

  const addFiles = (picked: PickedFile[]) => {
    setItems((prev) => {
      const next = picked.map<Item>(({ file }) => ({
        key: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 7)}`,
        file,
        title: titleFromFileName(file.name),
        problem: validateDocumentFile(file),
        status: 'pending',
        progress: 0,
      }));
      return single ? next.slice(0, 1) : [...prev, ...next];
    });
  };

  const patch = (key: string, p: Partial<Item>) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...p } : i)));

  const pendingValid = items.filter((i) => i.status === 'pending' && !i.problem && i.title.trim());
  const dateError = expiresAt && docDate && expiresAt < docDate ? 'A validade não pode ser antes da data do documento.' : null;
  const canSend = !sending && folderId !== '' && pendingValid.length > 0 && !dateError;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const done: Document[] = items.filter((i) => i.status === 'done' && i.doc).map((i) => i.doc as Document);
    let failed = false;
    for (const item of pendingValid) {
      if (controller.signal.aborted) break;
      patch(item.key, { status: 'uploading', progress: 0, message: undefined });
      try {
        const doc = await uploadDocumentFile(
          item.file,
          {
            folder_id: Number(folderId),
            title: item.title.trim(),
            description: description.trim() || undefined,
            doc_date: docDate || undefined,
            expires_at: expiresAt || undefined,
            remind_days: remindDays,
            tags: tags.trim() || undefined,
            ref_type: prefill?.ref_type,
            ref_id: prefill?.ref_id,
          },
          {
            signal: controller.signal,
            onProgress: ({ loaded, total }) => patch(item.key, { progress: total > 0 ? (loaded / total) * 100 : 0 }),
          },
        );
        done.push(doc);
        patch(item.key, { status: 'done', progress: 100, doc });
      } catch (err) {
        if (isUploadCancelled(err)) {
          patch(item.key, { status: 'pending', progress: 0, message: 'Envio cancelado.' });
          break;
        }
        failed = true;
        patch(item.key, { status: 'error', message: err instanceof Error ? err.message : 'Falha no envio.' });
      }
    }
    abortRef.current = null;
    setSending(false);
    // Tudo certo → devolve os documentos e fecha. Com falha ou cancelamento, o modal fica aberto
    // mostrando o que deu errado (os já enviados aparecem na lista quando o modal fechar).
    if (!failed && !controller.signal.aborted) onUploaded(done);
  };

  const cancel = () => {
    if (sending) abortRef.current?.abort();
    else onClose();
  };

  return (
    <Modal isOpen onClose={cancel} size="lg" closeOnBackdrop={!sending}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <UploadCloud size={18} className="text-slate-500" />
        <h2 className="text-h3">{single ? 'Enviar arquivo' : 'Enviar documentos'}</h2>
      </div>

      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-4">
        {(!single || items.length === 0) && (
          <Dropzone
            onFiles={addFiles}
            accept={DOCUMENT_ACCEPT}
            multiple={!single}
            disabled={sending}
            title={single ? 'Arraste o arquivo aqui ou clique para escolher' : 'Arraste os arquivos aqui ou clique para escolher'}
            hint="PDF, JPG, PNG, HEIC, WEBP, DOC, DOCX, XLS, XLSX ou TXT — até 50 MB cada."
          />
        )}

        {items.length > 0 && (
          <ul className="space-y-2">
            {items.map((i) => (
              <li key={i.key} className={clsx('rounded-lg border p-2 sm:p-3', i.problem || i.status === 'error' ? 'border-red-200 bg-red-50/40' : 'border-slate-200')}>
                <div className="flex items-start gap-2">
                  <FileTypeIcon fileName={i.file.name} contentType={i.file.type} />
                  <div className="min-w-0 flex-1 space-y-1">
                    <input
                      className="input-field"
                      value={i.title}
                      maxLength={200}
                      placeholder="Título"
                      disabled={i.status !== 'pending' || sending}
                      onChange={(e) => patch(i.key, { title: e.target.value })}
                    />
                    <p className="text-small [overflow-wrap:anywhere]">
                      {i.file.name} · {formatBytes(i.file.size)}
                    </p>
                    {i.problem && (
                      <p className="text-xs text-red-600 inline-flex items-center gap-1">
                        <AlertCircle size={12} /> {i.problem}
                      </p>
                    )}
                    {i.status === 'uploading' && <ProgressBar value={i.progress} showLabel />}
                    {i.status === 'done' && (
                      <p className="text-xs text-emerald-700 inline-flex items-center gap-1">
                        <CheckCircle2 size={12} /> Enviado
                      </p>
                    )}
                    {i.message && i.status !== 'done' && <p className="text-xs text-red-600">{i.message}</p>}
                  </div>
                  {i.status !== 'uploading' && i.status !== 'done' && (
                    <button
                      type="button"
                      className="p-1 text-slate-400 hover:text-red-600"
                      title="Remover da lista"
                      disabled={sending}
                      onClick={() => setItems((prev) => prev.filter((x) => x.key !== i.key))}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-slate-100 pt-4">
          <div className="sm:col-span-2">
            <label className={labelCls}>
              Pasta <span className="text-red-500">*</span>
            </label>
            <select
              className="select-field"
              value={folderId}
              disabled={sending || !!prefill?.lockFolder}
              onChange={(e) => setFolderId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Selecione…</option>
              {flat.map(({ folder, depth }) => (
                <option key={folder.id} value={folder.id}>
                  {`${'   '.repeat(depth)}${depth > 0 ? '↳ ' : ''}${folder.name}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Data do documento</label>
            <DateInputBR className="input-field w-full" value={docDate} onChange={setDocDate} disabled={sending} />
          </div>
          <div>
            <label className={labelCls}>Validade</label>
            <DateInputBR className="input-field w-full" value={expiresAt} onChange={setExpiresAt} disabled={sending} placeholder="sem validade" />
          </div>
          <div>
            <label className={labelCls}>Avisar com antecedência (dias)</label>
            <input
              type="number"
              min={0}
              max={365}
              className="input-field"
              value={remindDays}
              disabled={sending}
              onChange={(e) => setRemindDays(Math.min(365, Math.max(0, Number(e.target.value) || 0)))}
            />
          </div>
          <div>
            <label className={labelCls}>Tags</label>
            <input className="input-field" value={tags} maxLength={300} placeholder="separe por vírgula" disabled={sending} onChange={(e) => setTags(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Descrição</label>
            <textarea className="textarea-field" rows={2} maxLength={2000} value={description} disabled={sending} onChange={(e) => setDescription(e.target.value)} />
          </div>
          {dateError && <p className="sm:col-span-2 text-sm text-red-600">{dateError}</p>}
          {items.length > 1 && <p className="sm:col-span-2 text-small">Pasta, datas, aviso, tags e descrição valem para todos os arquivos da lista.</p>}
        </div>
      </div>

      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={cancel}>
          {sending ? 'Cancelar envio' : 'Fechar'}
        </Button>
        <Button variant="primary" icon={<UploadCloud />} onClick={send} loading={sending} disabled={!canSend}>
          {pendingValid.length > 1 ? `Enviar ${pendingValid.length} arquivos` : 'Enviar'}
        </Button>
      </div>
    </Modal>
  );
};
